import { FC, ReactNode, createElement } from 'react';
import React from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { useHover } from 'usehooks-ts';
import { fontColor, fontFamily, fontLineHeight, fontSize, fontWeight } from './text.constant';
import { FontFamily, FontWeight, Sizes, TextElement } from './text.types';
import { ColorPalette } from '../colors';
import { useTextOverflow } from '../utils/textOverflow';

interface TextI {
    family?: FontFamily;
    children: ReactNode | string;
    weight?: FontWeight;
    color?: ColorPalette;
    element?: TextElement;
    size?: Sizes;
    lineHeight?: Sizes;
    className?: string;
    htmlFor?: string;
    testId?: string;
    tooltip?: boolean;
}

export const Text: FC<TextI> = ({
    family = 'roboto',
    weight = 'normal',
    color = 'surface-high',
    element = 'p',
    size = '14',
    lineHeight,
    className,
    children,
    htmlFor,
    testId,
    tooltip = false,
}) => {
    const { contentRef, isOverflowing } = useTextOverflow(children as string, tooltip);
    const isHover = useHover(contentRef);

    const isStringOrNumber = (children: unknown) => {
        return typeof children === 'string' || typeof children === 'number';
    };
    const isAnArrayOfStringOrNumbers = (children: unknown) => {
        return Array.isArray(children) && children.every(isStringOrNumber);
    };
    const hasElementCustomized = (children: ReactNode | ReactNode[]) =>
        React.Children.toArray(children).some(
            (child) => typeof child === 'object' && Object.keys(child).includes('props'),
        );

    // Handle undefined children
    if (!children) {
        console.warn('Text component expected a string or number children but got undefined');
        return null;
    }

    if (isStringOrNumber(children) || isAnArrayOfStringOrNumbers(children) || hasElementCustomized(children)) {
        const classes = twMerge(
            'group relative',
            fontFamily[family],
            fontWeight[weight],
            fontColor[color],
            fontSize[size as Sizes],
            fontLineHeight[(lineHeight ? lineHeight : size) as Sizes],
            className,
        );

        const labelProps = element === 'label' && htmlFor ? { htmlFor } : {};
        const elementMeasures = contentRef.current?.getBoundingClientRect();

        return createElement(
            element,
            {
                className: classes,
                'data-test-id': testId,
                'data-testid': testId,
                ...labelProps,
                ref: contentRef,
            },
            [
                children,
                tooltip && elementMeasures && isHover && isOverflowing
                    ? createPortal(
                          createElement(
                              'div',
                              {
                                  style: {
                                      top: elementMeasures?.top - elementMeasures.height - 8,
                                      left: elementMeasures?.left + elementMeasures.width / 2,
                                  },
                                  className: twMerge(
                                      'absolute whitespace-nowrap text-xs hidden bg-slate-700 rounded p-1 z-[999] text-white',
                                      isHover && isOverflowing && 'block',
                                  ),
                              },
                              children,
                          ),
                          document.body,
                      )
                    : null,
            ],
        );
    }

    throw new Error('Child cant be a component it should be text or number. Child is ' + typeof children);
};

export default Text;
